import React from "react";
import { cls } from "@wss/cls";
import formatAsCurrency from "@wss/utilities/formatAsCurrency";
import { User } from "~/common/Store/models/User";
import MapPrice from "./MapPrice";
import PriceLabel from "./PriceLabelV2";
import StandardPrice from "./StandardPrice";
import { PricingProduct } from "~/ProductDetails/Pricing/PricingProduct";
import shouldDisplayMapStyle from "./shouldDisplayMapStyle";
import shouldDisplayQuantityDiscounts from "./shouldDisplayQuantityDiscounts";
import getLowestDisplayPrice from "~/common/Product/getLowestDisplayPrice";
import QuantityDiscountTable from "./QuantityDiscountTable";
import shouldDisplayRequiredCustomization from "~/common/Product/shouldDisplayRequiredCustomization";
import PlusShippingLevels from "./PlusShippingLevels";
import PlusPrice from "./PlusPrice";
import GoogleViewPrice from "./GoogleViewPrice";
import isActiveSale from "./isActiveSale";
import PlatinumPlusOverridePrice from "./PlatinumPlusOverridePrice";
import CreditKeyBanner from "../CreditKey/CreditKeyBanner";
import DisplaySalePrice from "./DisplaySalePrice";
interface Props {
  product: PricingProduct;
  user: User;
  isGoogleView?: boolean;
}
const Pricing = ({
  product,
  user,
  isGoogleView = false
}: Props) => {
  const {
    price,
    unitOfMeasure,
    isWebstaurantPlusEligible,
    hasWebPlusDiscountedShipping,
    hasFreeShipping,
    showBelowMinimumAdvertisedPriceForPlatinumAndPlusCustomers,
    minimumBuy,
    mustBuy,
    divideByUnit,
    divideQuantity,
    maximumCustomizationPrice,
    customizationRequired
  } = product;
  const displayMapStyle = shouldDisplayMapStyle(product, user);
  const divideByUnitCssClass = "text-sm inline-block";
  const getShowPlusPriceComparison = (lowestPrice: number) => {
    const p5 = product.price.minimumAdvertisedPriceOverride?.priceLevel5 ? product.price.minimumAdvertisedPriceOverride?.priceLevel5 : Infinity;
    // If sale price is lower than p5, we still display the comparison. Otherwise, we only display p5 if it is the lowest price.
    return p5 < product.price.price && p5 < lowestPrice;
  };
  const displayPricing = () => {
    const [displayQuantityDiscounts, discountsWithLowerPriceAndHigherStartBuy] = shouldDisplayQuantityDiscounts(product, user);
    if (displayMapStyle) {
      return <MapPrice product={product} user={user} isGoogleView={isGoogleView} quantityDiscounts={discountsWithLowerPriceAndHigherStartBuy} />;
    }
    const cheapestQuantityDiscount = displayQuantityDiscounts ? discountsWithLowerPriceAndHigherStartBuy[discountsWithLowerPriceAndHigherStartBuy.length - 1] : undefined;
    const isMinBuyOrMustBuyGreaterThanQuantityDiscount = displayQuantityDiscounts && cheapestQuantityDiscount && (product.minimumBuy >= cheapestQuantityDiscount.quantity || product.mustBuy >= cheapestQuantityDiscount.quantity);
    const lowestPrice = getLowestDisplayPrice(product, user, !isMinBuyOrMustBuyGreaterThanQuantityDiscount, true);
    const showCrossoutPrice = isActiveSale(price) && price.salePrice === lowestPrice;
    const showPlusPriceComparison = getShowPlusPriceComparison(lowestPrice);
    const displayPlatPlusOverridePrice = price.minimumAdvertisedPriceProperties?.type?.trim() && price.displayMinimumAdvertisedPrice && showBelowMinimumAdvertisedPriceForPlatinumAndPlusCustomers && (user.hasWebPlus || user.platinum?.enabled) && !displayQuantityDiscounts && !showPlusPriceComparison;
    if (displayPlatPlusOverridePrice) {
      return <>
          <PlatinumPlusOverridePrice regularPrice={price.price} platinumOrPlusPrice={lowestPrice} unitOfMeasure={unitOfMeasure} isPlusMember={!!user.hasWebPlus} divideByUnit={divideByUnit} divideQuantity={divideQuantity} divideByUnitCssClass="text-xs block " />
          <CreditKeyBanner price={lowestPrice} />
          <PlusShippingLevels hasFreeShipping={hasFreeShipping} hasWebPlusDiscountedShipping={hasWebPlusDiscountedShipping} isWebstaurantPlusEligible={isWebstaurantPlusEligible} />
        </>;
    }
    const displayRequiredCustomization = shouldDisplayRequiredCustomization(product);
    const mostExpensiveQuantityDiscount = displayQuantityDiscounts ? discountsWithLowerPriceAndHigherStartBuy[0] : undefined;
    let displayPrice = lowestPrice;
    if (isGoogleView && maximumCustomizationPrice && customizationRequired) {
      displayPrice = maximumCustomizationPrice;
    }
    const shouldDisplayQuantityDiscountsAndCustomization = displayQuantityDiscounts && !isGoogleView && !displayRequiredCustomization;
    const showPlusSignup = !(user.registered && user.hasWebPlus);
    return <div className="mb-4" data-sentry-component="displayPricing" data-sentry-source-file="index.tsx">
        {showPlusPriceComparison ? <>
            <PlusPrice p1Price={price.price} regularPrice={lowestPrice} plusMemberPrice={price.minimumAdvertisedPriceOverride.priceLevel5} unitOfMeasure={unitOfMeasure} showCrossoutPrice={showCrossoutPrice} divideByUnit={divideByUnit} divideQuantity={divideQuantity} showPlusSignup={showPlusSignup} user={user} isGoogleView={isGoogleView} minimumBuy={minimumBuy} mustBuy={mustBuy} />
            <CreditKeyBanner price={getLowestDisplayPrice(product, user, !displayQuantityDiscounts, !user?.hasWebPlus)} />
          </> : <>
            <PriceLabel product={product} user={user} mostExpensiveQuantityDiscount={mostExpensiveQuantityDiscount} isGoogleView={isGoogleView} quantityDiscounts={discountsWithLowerPriceAndHigherStartBuy} isMinBuyOrMustBuyGreaterThanQuantityDiscount={isMinBuyOrMustBuyGreaterThanQuantityDiscount} />
            {isGoogleView ? <GoogleViewPrice price={displayPrice} minimumBuy={minimumBuy} mustBuy={mustBuy} divideByUnit={divideByUnit} divideQuantity={divideQuantity} unitOfMeasure={minimumBuy <= 1 && mustBuy <= 1 ? unitOfMeasure : undefined} /> : <>
                {!displayQuantityDiscounts && showCrossoutPrice ? <DisplaySalePrice price={price.price} priceLabel={formatAsCurrency.formatIfUndefined(price.price)} salePrice={price.salePrice} unitOfMeasure={unitOfMeasure} divideByUnit={divideByUnit} divideQuantity={divideQuantity} salePricingClassNames="sale-price" saleBadgeExtraClassNames={cls("mr-1", "mt-0.5", "px-3/4", "py-0.5", "text-sm")} regularPricingClassNames={cls("price", "text-left")} cssClass={divideByUnitCssClass} /> : <StandardPrice price={displayPrice} unitOfMeasure={unitOfMeasure} divideByUnit={divideByUnit} divideQuantity={divideQuantity} divideByUnitCssClass={divideByUnitCssClass} />}
              </>}
          </>}
        <PlusShippingLevels hasFreeShipping={hasFreeShipping} hasWebPlusDiscountedShipping={hasWebPlusDiscountedShipping} isWebstaurantPlusEligible={isWebstaurantPlusEligible} data-sentry-element="PlusShippingLevels" data-sentry-source-file="index.tsx" />
        {shouldDisplayQuantityDiscountsAndCustomization && !isMinBuyOrMustBuyGreaterThanQuantityDiscount && <QuantityDiscountTable product={product} quantityDiscounts={discountsWithLowerPriceAndHigherStartBuy} lowestPriceWithoutQtyDiscount={lowestPrice} includeCheapestDiscount={showPlusPriceComparison} showCrossoutPrice={showCrossoutPrice} divideByUnit={divideByUnit} divideQuantity={divideQuantity} />}
      </div>;
  };
  return <div className="pricing relative z-0" data-sentry-component="Pricing" data-sentry-source-file="index.tsx">{displayPricing()}</div>;
};
export default Pricing;